// Shared building blocks for the public marketing site + auth pages (Accss design system v2).
// Built on top of components/ui (IconTile, Pill...) and theme.aw tokens.
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { alpha, Box, Card, Container, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import AccessibleForwardRoundedIcon from "@mui/icons-material/AccessibleForwardRounded";
import { Pill } from "../../components/ui";

/** Accent color for small text: the brand blue in light mode, a lighter blue in dark mode (AA on dark surfaces). */
export const accentText = (t) => (t.palette.mode === "dark" ? "#7cb4f5" : t.palette.primary.main);

/** Accss logo: blue tile + wordmark. */
export function BrandMark({ to = "/", size = 32, onClick, sx }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      onClick={onClick}
      aria-label="Accss"
      sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, color: "text.primary", textDecoration: "none", ...sx }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: size,
          height: size,
          borderRadius: size >= 36 ? "12px" : "10px",
          bgcolor: "primary.main",
          color: "#fff",
          display: "grid",
          placeItems: "center",
          boxShadow: (t) => `0 6px 14px -6px ${alpha(t.palette.primary.main, 0.7)}`,
        }}
      >
        <AccessibilityNewRoundedIcon sx={{ fontSize: Math.round(size * 0.62) }} />
      </Box>
      <Box component="span" aria-hidden="true" sx={{ fontWeight: 800, fontSize: size >= 36 ? "1.35rem" : "1.2rem", letterSpacing: "-0.02em" }}>
        Accss
      </Box>
    </Box>
  );
}

/** Arrow that points "forward" in the current reading direction. */
export function DirArrow(props) {
  const theme = useTheme();
  return theme.direction === "rtl" ? <ArrowBackRoundedIcon {...props} /> : <ArrowForwardRoundedIcon {...props} />;
}

/** Vertical page section with consistent rhythm. tone: plain | soft */
export function Section({ id, tone = "plain", children, sx, containerSx, maxWidth = "lg", labelledBy }) {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={labelledBy}
      sx={{
        py: { xs: 7, md: 11 },
        bgcolor: tone === "soft" ? (t) => t.aw.bg : "background.paper",
        borderBlock: tone === "soft" ? "1px solid" : "none",
        borderColor: "divider",
        ...sx,
      }}
    >
      <Container maxWidth={maxWidth} sx={containerSx}>
        {children}
      </Container>
    </Box>
  );
}

/** Eyebrow + h2 + subtitle. */
export function SectionHeading({ id, eyebrow, title, subtitle, align = "center", sx }) {
  const centered = align === "center";
  return (
    <Box sx={{ textAlign: centered ? "center" : "start", maxWidth: centered ? 720 : 640, mx: centered ? "auto" : 0, mb: { xs: 5, md: 7 }, ...sx }}>
      {eyebrow ? (
        <Typography
          component="p"
          sx={{ color: accentText, fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.02em", mb: 1.25 }}
        >
          {eyebrow}
        </Typography>
      ) : null}
      <Typography id={id} variant="h2" sx={{ fontSize: { xs: "1.75rem", md: "2.35rem" } }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: "text.secondary", mt: 1.75, fontSize: { xs: "1rem", md: "1.075rem" }, lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Top band of inner marketing pages (h1). */
export function PageHero({ eyebrow, title, subtitle, actions, icon, children }) {
  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: (t) => t.aw.softer,
        "--mkt-dot": (t) => (t.palette.mode === "dark" ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"),
      }}
    >
      <Box className="mkt-grid-bg" aria-hidden="true" sx={{ position: "absolute", inset: 0 }} />
      <Container sx={{ position: "relative", py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: 780 }}>
          {eyebrow ? (
            <Pill sx={{ mb: 2, px: 1.5, py: 0.6, fontSize: "0.8rem" }}>
              {icon}
              {eyebrow}
            </Pill>
          ) : null}
          <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.9rem" } }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ color: "text.secondary", mt: 2, fontSize: { xs: "1rem", md: "1.125rem" }, lineHeight: 1.75 }}>
              {subtitle}
            </Typography>
          ) : null}
          {actions ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3.5 }}>
              {actions}
            </Stack>
          ) : null}
          {children}
        </Box>
      </Container>
    </Box>
  );
}

/** Removes a leading "• " that some translation strings carry. */
export const stripBullet = (s) => String(s || "").replace(/^\s*[•·-]\s*/, "");

/** List with small check marks. */
export function CheckList({ items, dense, tone = "primary", sx }) {
  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: dense ? 1 : 1.5, ...sx }}>
      {items.map((it, i) => (
        <Box component="li" key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
          <Box
            aria-hidden="true"
            sx={{
              mt: "3px",
              width: 20,
              height: 20,
              flex: "none",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: `${tone}.main`,
              bgcolor: (t) => alpha(t.palette[tone].main, t.palette.mode === "dark" ? 0.2 : 0.1),
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 14 }} />
          </Box>
          <Typography component="span" sx={{ color: "text.secondary", lineHeight: 1.6, fontSize: dense ? "0.925rem" : "1rem" }}>
            {stripBullet(it)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Bordered card with hover lift (for link cards / feature cards). */
export function FeatureCard({ children, sx, interactive = false, ...rest }) {
  return (
    <Card
      {...rest}
      sx={{
        p: { xs: 3, md: 3.5 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow .2s ease, transform .2s ease, border-color .2s ease",
        ...(interactive
          ? {
              "&:hover": { boxShadow: (t) => t.aw.shadowMd, borderColor: (t) => t.aw.borderStrong, transform: "translateY(-2px)" },
            }
          : {}),
        ...sx,
      }}
    >
      {children}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Hero illustration: the v2 accessibility panel + floating buttons.   */
/* Pure JSX/CSS, decorative (exposed to AT as a single labelled image). */
/* ------------------------------------------------------------------ */

function SkeletonLine({ w = "100%", h = 10, sx }) {
  return <Box sx={{ width: w, height: h, borderRadius: 999, bgcolor: (t) => t.aw.soft, ...sx }} />;
}

function MockProfile({ icon, title, desc, active, activeLabel }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        p: 1.25,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: active ? "primary.main" : (t) => t.aw.border,
        bgcolor: active ? (t) => t.aw.accentSoft : (t) => t.aw.softer,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flex: "none",
          borderRadius: "9px",
          display: "grid",
          placeItems: "center",
          color: active ? "#fff" : "primary.main",
          bgcolor: active ? "primary.main" : "background.paper",
          border: active ? "none" : "1px solid",
          borderColor: (t) => t.aw.border,
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ fontWeight: 700, fontSize: "0.8rem", color: "text.primary", lineHeight: 1.3 }}>{title}</Box>
        <Box sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.35, mt: 0.25 }}>{desc}</Box>
      </Box>
      {active ? (
        <Box sx={{ flex: "none", fontSize: "0.65rem", fontWeight: 700, color: "#fff", bgcolor: "primary.main", px: 1, py: 0.25, borderRadius: 999 }}>
          {activeLabel}
        </Box>
      ) : null}
    </Box>
  );
}

export function WidgetMockup() {
  const { t } = useTranslation();
  const tabs = [t("mk_w_tab_quick"), t("mk_w_tab_text"), t("mk_w_tab_colors"), t("mk_w_tab_nav")];

  return (
    <Box
      role="img"
      aria-label={t("mk_w_alt")}
      sx={{ position: "relative", width: "100%", maxWidth: 560, mx: "auto", pb: { xs: 3, sm: 4 } }}
    >
      {/* soft glow */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: "8% 6% 4% 6%",
          borderRadius: "40px",
          background: (th) => `radial-gradient(60% 60% at 50% 40%, ${alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.28 : 0.16)}, transparent 70%)`,
          filter: "blur(30px)",
        }}
      />
      {/* Browser window (the customer's site) */}
      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          borderRadius: "18px",
          border: "1px solid",
          borderColor: (th) => th.aw.border,
          bgcolor: "background.paper",
          boxShadow: (th) => th.aw.shadowLg,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, height: 40, borderBottom: "1px solid", borderColor: (th) => th.aw.border, bgcolor: (th) => th.aw.softer }}>
          <Stack direction="row" spacing={0.75} sx={{ direction: "ltr" }}>
            {["#f87171", "#fbbf24", "#34d399"].map((c) => (
              <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c, opacity: 0.85 }} />
            ))}
          </Stack>
          <Box sx={{ flex: 1, mx: 2, height: 22, borderRadius: 999, bgcolor: (th) => th.aw.soft, display: "flex", alignItems: "center", px: 1.5, fontSize: "0.7rem", color: "text.secondary", direction: "ltr" }}>
            your-site.co.il
          </Box>
        </Stack>

        <Box sx={{ position: "relative", height: { xs: 530, sm: 500 } }}>
          {/* fake site content on the far side */}
          <Box sx={{ position: "absolute", insetInlineEnd: 24, top: 28, width: { xs: "30%", sm: "36%" }, display: "grid", gap: 1.25 }}>
            <SkeletonLine w="55%" h={8} sx={{ bgcolor: (th) => alpha(th.palette.primary.main, 0.25) }} />
            <SkeletonLine w="95%" h={16} />
            <SkeletonLine w="80%" h={16} />
            <SkeletonLine w="100%" h={8} sx={{ mt: 1 }} />
            <SkeletonLine w="92%" h={8} />
            <SkeletonLine w="70%" h={8} />
            <Box sx={{ mt: 1.5, width: "60%", height: 26, borderRadius: "8px", bgcolor: (th) => alpha(th.palette.primary.main, 0.85) }} />
            <Box sx={{ mt: 2, height: 110, borderRadius: "12px", bgcolor: (th) => th.aw.soft, border: "1px solid", borderColor: (th) => th.aw.border }} />
            <SkeletonLine w="85%" h={8} sx={{ mt: 1 }} />
            <SkeletonLine w="60%" h={8} />
          </Box>

          {/* the panel */}
          <Box
            sx={{
              position: "absolute",
              top: 18,
              insetInlineStart: { xs: 12, sm: 20 },
              width: { xs: "78%", sm: 316 },
              borderRadius: "18px",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: (th) => th.aw.border,
              boxShadow: (th) => th.aw.shadowLg,
              p: 1.75,
              display: "grid",
              gap: 1.25,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center", flex: "none" }}>
                <AccessibilityNewRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.2 }}>{t("mk_w_title")}</Box>
                <Box sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.3 }}>{t("mk_w_sub")}</Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 0.9, height: 28, borderRadius: "9px", border: "1px solid", borderColor: (th) => th.aw.border, fontSize: "0.65rem", fontWeight: 700 }}>
                <LanguageRoundedIcon sx={{ fontSize: 14 }} />
                {t("mk_w_lang")}
              </Box>
              <Box sx={{ width: 28, height: 28, borderRadius: "9px", border: "1px solid", borderColor: (th) => th.aw.border, display: "grid", placeItems: "center" }}>
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Stack>

            <Box sx={{ height: 34, borderRadius: "10px", border: "1px solid", borderColor: (th) => th.aw.borderStrong, display: "flex", alignItems: "center", gap: 1, px: 1.25, color: "text.secondary", fontSize: "0.75rem" }}>
              <SearchRoundedIcon sx={{ fontSize: 16 }} />
              {t("mk_w_search")}
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.72rem", fontWeight: 700, color: "primary.main" }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main" }} />
                {t("mk_w_active")}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, height: 24, borderRadius: 999, border: "1px solid", borderColor: (th) => th.aw.border, fontSize: "0.68rem", fontWeight: 600 }}>
                <RestartAltRoundedIcon sx={{ fontSize: 14 }} />
                {t("mk_w_reset")}
              </Box>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, p: 0.5, borderRadius: "11px", bgcolor: (th) => th.aw.soft }}>
              {tabs.map((label, i) => (
                <Box
                  key={label}
                  sx={{
                    textAlign: "center",
                    py: 0.6,
                    borderRadius: "8px",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: i === 0 ? "primary.main" : "text.secondary",
                    bgcolor: i === 0 ? "background.paper" : "transparent",
                    boxShadow: i === 0 ? (th) => th.aw.shadowSm : "none",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>

            <Box sx={{ fontSize: "0.72rem", fontWeight: 700, color: "text.secondary", mt: 0.25 }}>{t("mk_w_help")}</Box>
            <MockProfile icon={<VisibilityOutlinedIcon />} title={t("mk_w_p1_t")} desc={t("mk_w_p1_d")} />
            <MockProfile icon={<MenuBookOutlinedIcon />} title={t("mk_w_p2_t")} desc={t("mk_w_p2_d")} active activeLabel={t("mk_w_on")} />
          </Box>

          {/* floating buttons: cookie + accessibility */}
          <Stack direction="row" spacing={1.25} sx={{ position: "absolute", bottom: 18, insetInlineStart: { xs: 12, sm: 20 } }}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center", boxShadow: (th) => `0 10px 22px -8px ${alpha(th.palette.primary.main, 0.8)}` }}>
              <AccessibleForwardRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center", boxShadow: (th) => `0 10px 22px -8px ${alpha(th.palette.primary.main, 0.8)}` }}>
              <CookieOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Auth shell: centered card on a calm background.                      */
/* ------------------------------------------------------------------ */
export function AuthShell({ title, subtitle, children, footer, icon, maxWidth = 440 }) {
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "auto", md: "calc(100vh - 64px - 1px)" },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        px: 2,
        py: { xs: 5, md: 8 },
        "--mkt-dot": (t) => (t.palette.mode === "dark" ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"),
      }}
    >
      <Box className="mkt-grid-bg" aria-hidden="true" sx={{ position: "absolute", inset: 0 }} />
      <Box sx={{ position: "relative", width: "100%", maxWidth }}>
        <Card sx={{ p: { xs: 3, sm: 4.5 }, boxShadow: (t) => t.aw.shadowMd }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center", mb: 3.5 }}>
            {icon || (
              <Box
                aria-hidden="true"
                sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center", boxShadow: (t) => `0 10px 22px -10px ${alpha(t.palette.primary.main, 0.9)}` }}
              >
                <AccessibilityNewRoundedIcon sx={{ fontSize: 28 }} />
              </Box>
            )}
            <Typography variant="h1" sx={{ fontSize: { xs: "1.5rem", sm: "1.7rem" }, fontWeight: 800 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ color: "text.secondary", fontSize: "0.95rem" }}>{subtitle}</Typography>
            ) : null}
          </Stack>
          {children}
        </Card>
        {footer ? (
          <Box sx={{ mt: 3, textAlign: "center", color: "text.secondary", fontSize: "0.9rem" }}>{footer}</Box>
        ) : null}
      </Box>
    </Box>
  );
}

/** "Or" divider between social and email sign-in. */
export function OrDivider({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "text.secondary", fontSize: "0.8rem" }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
      {label}
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
    </Box>
  );
}

/** Google "G" mark (inline SVG, brand colors). */
export function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** Centered status card (payment results etc.). */
export function StatusCard({ icon, tone = "primary", title, children, actions, maxWidth = 560 }) {
  const theme = useTheme();
  const c = theme.palette[tone] ? theme.palette[tone].main : theme.palette.primary.main;
  return (
    <Box sx={{ px: 2, py: { xs: 4, md: 8 }, display: "flex", justifyContent: "center" }}>
      <Card sx={{ width: "100%", maxWidth, p: { xs: 3, sm: 5 }, textAlign: "center", boxShadow: (t) => t.aw.shadowMd }}>
        {icon ? (
          <Box
            aria-hidden="true"
            sx={{ width: 64, height: 64, mx: "auto", mb: 2.5, borderRadius: "20px", display: "grid", placeItems: "center", color: c, bgcolor: alpha(c, theme.palette.mode === "dark" ? 0.2 : 0.1), "& svg": { fontSize: 34 } }}
          >
            {icon}
          </Box>
        ) : null}
        {title ? (
          <Typography variant="h1" sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" }, mb: 1.5 }}>
            {title}
          </Typography>
        ) : null}
        <Box sx={{ display: "grid", gap: 2, textAlign: "start" }}>{children}</Box>
        {actions ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 3.5 }}>
            {actions}
          </Stack>
        ) : null}
      </Card>
    </Box>
  );
}
